import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  ChevronsUpDown,
  Plus,
  Settings,
  LogOut,
  User,
  SlidersHorizontal,
} from '@/components/icons';
import {
  Avatar,
  AvatarFallback,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@erp/ui';
import { useAuth } from '@/lib/auth-context';
import { useIsAdmin } from '@/hooks/use-admin';
import { useCompanySettings } from '@/hooks/use-settings';
import { useAdminNotifications } from '@/hooks/use-storefront';
import { useEnabledModules } from '@/hooks/use-modules';
import { useProPendingCounts } from '@/hooks/use-pro-pending-counts';
import { useBrandTheme } from '@/lib/brand-theme-provider';
import { SmartLink } from '@/lib/nav-prefs/smart-link';
import { useSmartNavigate } from '@/lib/nav-prefs/use-smart-navigate';
import { NAV_GROUPS, type NavGroup, type NavItem } from './nav-config';
import type { ModuleKey } from '@erp/data-access/interfaces';
import { DefaultBrandIcon } from '@/components/DefaultBrandMark';
import {
  DEFAULT_BRAND_ICON,
  DEFAULT_BRAND_LOGO,
  DEFAULT_BRAND_LOGO_DARK,
  getBrandInitial,
  isDefaultBrandName,
} from '@/lib/default-brand';

const LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ar', label: 'العربية', flag: '🇹🇳' },
] as const;

function filterAdminItems(items: NavItem[], isAdmin: boolean): NavItem[] {
  return items
    .filter((it) => !it.adminOnly || isAdmin)
    .map((it) =>
      it.children
        ? { ...it, children: filterAdminItems(it.children, isAdmin) }
        : it,
    );
}

function filterAdminGroups(groups: NavGroup[], isAdmin: boolean): NavGroup[] {
  return groups
    .map((g) => ({ ...g, items: filterAdminItems(g.items, isAdmin) }))
    .filter((g) => g.items.length > 0);
}

/** Hide items whose `module` key is missing from the enabled set. Recurses
 *  into children so disabled sub-modules vanish even if their parent stays. */
function filterModuleItems(
  items: NavItem[],
  enabled: Set<ModuleKey>,
): NavItem[] {
  return items
    .filter((it) => !it.module || enabled.has(it.module))
    .map((it) =>
      it.children
        ? { ...it, children: filterModuleItems(it.children, enabled) }
        : it,
    );
}

function filterModuleGroups(
  groups: NavGroup[],
  enabled: Set<ModuleKey>,
): NavGroup[] {
  return groups
    .filter((g) => !g.module || enabled.has(g.module))
    .map((g) => ({ ...g, items: filterModuleItems(g.items, enabled) }))
    .filter((g) => g.items.length > 0);
}

function applyBadges(
  items: NavItem[],
  badges: Record<string, number | undefined>,
): NavItem[] {
  return items.map((it) => {
    const next: NavItem = { ...it };
    const badge = badges[it.path];
    if (badge && badge > 0) next.badge = badge;
    if (it.children) next.children = applyBadges(it.children, badges);
    return next;
  });
}

function applyBadgesToGroups(
  groups: NavGroup[],
  badges: Record<string, number | undefined>,
): NavGroup[] {
  return groups.map((g) => ({ ...g, items: applyBadges(g.items, badges) }));
}

function isPathActive(
  currentPath: string,
  itemPath: string,
  exact = false,
): boolean {
  if (exact) return currentPath === itemPath;
  return currentPath === itemPath || currentPath.startsWith(itemPath + '/');
}

export function AppSidebar() {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const settingsQuery = useCompanySettings();
  const { data: settings } = settingsQuery;
  const { resolvedMode } = useBrandTheme();
  const { data: unreadNotifs = [] } = useAdminNotifications({
    limit: 100,
    isRead: false,
  });
  const { enabled: enabledModules } = useEnabledModules();
  const proCounts = useProPendingCounts();

  // Mobile UX: auto-close the slide-over drawer the moment the route
  // changes. Without this the user has to dismiss the overlay manually
  // after every link tap. Watching `pathname` covers Link clicks,
  // navigate() calls, and the back/forward buttons in one place.
  const { isMobile, setOpenMobile } = useSidebar();
  const location = useLocation();
  useEffect(() => {
    if (isMobile) setOpenMobile(false);
  }, [location.pathname, isMobile, setOpenMobile]);
  const unreadOrders = unreadNotifs.filter(
    (n) => n.kind === 'order_new',
  ).length;
  // Filter order: admin first (some items are admin-only and must never
  // appear for non-admins regardless of module state), then modules.
  const adminFiltered = filterAdminGroups(NAV_GROUPS, isAdmin);
  const moduleFiltered = filterModuleGroups(adminFiltered, enabledModules);
  const groups = applyBadgesToGroups(moduleFiltered, {
    '/storefront/orders': unreadOrders,
    '/storefront': unreadOrders,
    '/pro': proCounts.proTotal,
    '/pro/customers': proCounts.pendingPros,
    '/pro/commandes': proCounts.pendingCommandes,
    '/pro/commissions': proCounts.pendingCommissions,
  });

  const brandingResolved = settingsQuery.isSuccess || settingsQuery.isError;
  const brandName = brandingResolved ? settings?.softwareName || t('app.name') : '';
  const tagline = t('app.tagline');
  const brandInitial = getBrandInitial(brandName);
  const sidebarBrandDisplay = settings?.sidebarBrandDisplay ?? 'logo_with_name';
  const themedLogoUrl =
    brandingResolved && resolvedMode === 'dark'
      ? settings?.logoDarkUrl ?? settings?.logoUrl ?? null
      : brandingResolved
        ? settings?.logoUrl ?? settings?.logoDarkUrl ?? null
        : null;
  const defaultExpandedLogoUrl =
    brandingResolved
      ? resolvedMode === 'dark' ? DEFAULT_BRAND_LOGO_DARK : DEFAULT_BRAND_LOGO
      : null;
  const hasTenantLogo = Boolean(themedLogoUrl || settings?.logoSmallUrl);
  const expandedLogoUrl =
    themedLogoUrl ?? settings?.logoSmallUrl ?? defaultExpandedLogoUrl;
  const collapsedLogoUrl =
    settings?.logoSmallUrl ?? themedLogoUrl ?? (brandingResolved ? DEFAULT_BRAND_ICON : null);
  const showExpandedBrandText =
    sidebarBrandDisplay === 'logo_with_name' &&
    (hasTenantLogo || !isDefaultBrandName(brandName));

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/">
                <div className="hidden aspect-square size-8 items-center justify-center overflow-hidden rounded-[10px] bg-sidebar-primary text-sidebar-primary-foreground group-data-[collapsible=icon]:flex">
                  {!brandingResolved ? (
                    <div className="size-full animate-pulse rounded-[10px] bg-sidebar-accent" aria-hidden="true" />
                  ) : collapsedLogoUrl ? (
                    <img
                      src={collapsedLogoUrl}
                      alt={brandName}
                      className="size-full object-contain p-0.5"
                    />
                  ) : (
                    <DefaultBrandIcon
                      initial={brandInitial}
                      variant="sidebar"
                      className="size-full rounded-[10px] text-xs"
                    />
                  )}
                </div>
                <div className="flex min-w-0 flex-1 items-center gap-3 group-data-[collapsible=icon]:hidden">
                  {!brandingResolved ? (
                    <div className="grid min-w-0 flex-1 gap-2" aria-hidden="true">
                      <div className="h-4 w-28 animate-pulse rounded-md bg-sidebar-accent" />
                      <div className="h-3 w-20 animate-pulse rounded-md bg-sidebar-accent/70" />
                    </div>
                  ) : !showExpandedBrandText ? (
                    expandedLogoUrl ? (
                      <img
                        src={expandedLogoUrl}
                        alt={brandName}
                        className="max-h-9 max-w-[158px] object-contain"
                      />
                    ) : (
                      <DefaultBrandIcon
                        initial={brandInitial}
                        variant="sidebar"
                        className="text-xs"
                      />
                    )
                  ) : (
                    <>
                      <div className="flex h-8 max-w-14 shrink-0 items-center justify-center overflow-hidden rounded-[10px] bg-sidebar-primary text-sidebar-primary-foreground">
                        {expandedLogoUrl ? (
                          <img
                            src={collapsedLogoUrl ?? expandedLogoUrl}
                            alt={brandName}
                            className="max-h-8 max-w-14 object-contain p-0.5"
                          />
                        ) : (
                          <DefaultBrandIcon
                            initial={brandInitial}
                            variant="sidebar"
                            className="size-full rounded-[10px] text-xs"
                          />
                        )}
                      </div>
                      <div className="grid min-w-0 flex-1 text-start text-sm leading-tight">
                        <span className="truncate font-semibold">
                          {brandName}
                        </span>
                        <span className="truncate text-xs text-sidebar-foreground/70">
                          {tagline}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {groups.map((group, idx) => (
          <NavGroupSection key={idx} group={group} />
        ))}
      </SidebarContent>

      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}

function NavBadge({ count }: { count: number }) {
  const label = count > 99 ? '99+' : String(count);
  return (
    <span className="ms-auto min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-red-500 text-white shrink-0">
      {label}
    </span>
  );
}

function NavGroupSection({ group }: { group: NavGroup }) {
  const { t } = useTranslation();

  return (
    <SidebarGroup>
      {group.labelKey && (
        <SidebarGroupLabel>{t(group.labelKey)}</SidebarGroupLabel>
      )}
      <SidebarMenu>
        {group.items.map((item) => (
          <NavItemRow key={item.path + item.labelKey} item={item} />
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}

function NavItemRow({ item }: { item: NavItem }) {
  const { t } = useTranslation();
  const location = useLocation();

  if (item.children && item.children.length > 0) {
    const isAnyChildActive = item.children.some((c) =>
      isPathActive(location.pathname, c.path),
    );

    // Two layouts depending on whether the parent path is a real route:
    // - navigable: label is a Link to the parent path; chevron is a separate
    //   toggle. Only the matching child highlights — no double focus state.
    // - not navigable: label IS the toggle (legacy behaviour for Sales /
    //   Purchases / Settings whose `/sales` etc. don't have a route).
    if (item.navigable) {
      return (
        <Collapsible
          asChild
          defaultOpen={isAnyChildActive}
          className="group/collapsible"
        >
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip={t(item.labelKey)}>
              <SmartLink to={item.path}>
                {item.icon}
                <span>{t(item.labelKey)}</span>
                {item.badge ? <NavBadge count={item.badge} /> : null}
              </SmartLink>
            </SidebarMenuButton>
            <CollapsibleTrigger asChild>
              <SidebarMenuAction
                className="data-[state=open]:rotate-90 transition-transform"
                aria-label={t(item.labelKey)}
              >
                <ChevronRight className="rtl:-scale-x-100" />
              </SidebarMenuAction>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarMenuSub>
                {item.children.map((child) => (
                  <NavSubItemRow
                    key={child.path + child.labelKey}
                    item={child}
                  />
                ))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      );
    }

    return (
      <Collapsible
        asChild
        defaultOpen={isAnyChildActive}
        className="group/collapsible"
      >
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton
              tooltip={t(item.labelKey)}
              isActive={isAnyChildActive}
            >
              {item.icon}
              <span>{t(item.labelKey)}</span>
              {item.badge ? <NavBadge count={item.badge} /> : null}
              <ChevronRight className="ms-auto transition-transform group-data-[state=open]/collapsible:rotate-90 rtl:-scale-x-100" />
            </SidebarMenuButton>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {item.children.map((child) => (
                <NavSubItemRow key={child.path + child.labelKey} item={child} />
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    );
  }

  const exact = item.path === '/';
  const active = isPathActive(location.pathname, item.path, exact);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={t(item.labelKey)} isActive={active}>
        <SmartLink to={item.path}>
          {item.icon}
          <span>{t(item.labelKey)}</span>
          {item.badge ? <NavBadge count={item.badge} /> : null}
        </SmartLink>
      </SidebarMenuButton>
      {item.addPath && (
        <SidebarMenuAction asChild showOnHover>
          <SmartLink to={item.addPath} aria-label={t('common.create')}>
            <Plus />
          </SmartLink>
        </SidebarMenuAction>
      )}
    </SidebarMenuItem>
  );
}

function NavSubItemRow({ item }: { item: NavItem }) {
  const { t } = useTranslation();
  const location = useLocation();
  const smartNavigate = useSmartNavigate();
  // Sub-items use exact match: a generic parent path like `/caisse` (POS)
  // would otherwise also match deeper sibling paths like `/caisse/registers`,
  // and both would highlight at the same time.
  const active = isPathActive(location.pathname, item.path, true);

  return (
    <SidebarMenuSubItem className="group/sub">
      <SidebarMenuSubButton asChild isActive={active}>
        <SmartLink to={item.path} className="flex items-center gap-2">
          <span className="flex-1 truncate">{t(item.labelKey)}</span>
          {item.badge ? <NavBadge count={item.badge} /> : null}
        </SmartLink>
      </SidebarMenuSubButton>
      {item.addPath && (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            smartNavigate(item.addPath!);
          }}
          aria-label={t('common.create')}
          className="absolute end-1 top-0.5 opacity-0 group-hover/sub:opacity-100 transition-opacity text-sidebar-foreground"
        >
          <Plus className="size-3" />
        </Button>
      )}
    </SidebarMenuSubItem>
  );
}

function NavUser() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { isMobile } = useSidebar();
  const navigate = useNavigate();

  if (!user) return null;

  const userInitial = user.email?.charAt(0).toUpperCase() ?? 'U';
  const userName = user.email?.split('@')[0] ?? 'User';

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="size-8 rounded-full">
                <AvatarFallback className="rounded-full text-xs font-semibold">
                  {userInitial}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-start text-sm leading-tight">
                <span className="truncate font-medium capitalize">
                  {userName}
                </span>
                <span className="truncate text-xs text-sidebar-foreground/70">
                  {user.email}
                </span>
              </div>
              <ChevronsUpDown className="ms-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-start text-sm">
                <Avatar className="size-8 rounded-full">
                  <AvatarFallback className="rounded-full text-xs font-semibold">
                    {userInitial}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-start text-sm leading-tight">
                  <span className="truncate font-medium capitalize">
                    {userName}
                  </span>
                  <span className="truncate text-xs text-muted-foreground">
                    {user.email}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {LANGUAGES.map((lang) => (
              <DropdownMenuItem
                key={lang.code}
                onClick={() => i18n.changeLanguage(lang.code)}
                className={i18n.language === lang.code ? 'bg-accent' : ''}
              >
                <span className="me-1">{lang.flag}</span>
                {lang.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings/company')}>
              <Settings size={14} />
              {t('nav.settings')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings/branding')}>
              <User size={14} />
              {t('nav.settingsBranding')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/app-builder')}>
              <SlidersHorizontal size={14} />
              {t('nav.settingsAppBuilder', {
                defaultValue: 'App Builder mobile',
              })}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={logout}
              className="text-destructive focus:text-destructive"
            >
              <LogOut size={14} />
              {t('auth.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
